import { MirrorData, SetMirrorData } from '@type/job-add';
import { useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { RecordInterface } from '@type/form-types';
import { RepostingNumber } from '@components/templates/job-add/edit/form/RepostingNumber';
import { TailwindSpinner } from '@components/common';
import { PercentBadgeIcon } from '@heroicons/react/20/solid';
import { useSwrCalculatePrice } from '@hooks/useSwrApi/endpoints/publication_v2/useSwrCalculatePrice';

/** Component props */
interface JobActivationPriceProps {
  locale: Locale;
  jobSlug: string | undefined;
  packageId: string | undefined;
  record: RecordInterface;
  initialRepostingNumber?: number;
  repostingCompatible?: boolean;
  removeRepostingFromSubmit?: boolean;
  activationPeriod?: string;
  reqMirrorData?: Pick<
  MirrorData,
  'title' | 'vacancies' | 'addresses' | 'salaryMin' | 'salaryMax'
  | 'salaryConfidential' | 'workPlacement' | 'domain' | 'ownApplyUrl' | 'repostingNumber' | 'withoutCharge'
  >;
  setMirrorData?: SetMirrorData;
  inActivePeriod?: boolean;
  selectedActivation?: ApiSchema<'ActivationBundle'>;
}

/**
 * @description Job activation price component.
 * - `locale`: The current app locale.
 * - `jobSlug`: The job slug.
 * - `packageId`: The currently selected package id.
 * - `record`: The form record.
 * - `initialRepostingNumber`: The initial reposting number.
 * - `repostingCompatible`: If the job has reposting.
 * - `removeRepostingFromSubmit`: If the reposting number should be omitted from the form submit.
 * - `activationPeriod`: The activation period.
 * - `reqMirrorData`: The required form mirror data.
 * - `setMirrorData`: The set mirror data function.
 * - `inActivePeriod`: If the job is in an active period.
 * - `selectedActivation`: The selected activation bundle.
 */
export const JobPrice = (props: JobActivationPriceProps) => {
  // Destructure props
  const {
    locale,
    jobSlug = '',
    packageId = '',
    record,
    initialRepostingNumber = 0,
    repostingCompatible = false,
    removeRepostingFromSubmit = true,
    activationPeriod,
    reqMirrorData,
    setMirrorData,
    inActivePeriod,
    selectedActivation,
  } = props;

  // Helpers
  const { t } = useTranslation('common');

  // Get the reposting number from a child
  const [repostingNumber, setRepostingNumber] = useState<number>(initialRepostingNumber);

  // Reset the reposting number if reposting is not compatible
  useEffect(() => {
    if (!repostingCompatible) setRepostingNumber(0);
  }, [repostingCompatible]);

  // SWR, get the price data
  const { data: priceData, isLoading } = useSwrCalculatePrice({
    locale,
    jobSlug,
    shouldFetch: !!reqMirrorData?.title && !!reqMirrorData?.vacancies,
    payload: {
      packageId,
      ...(repostingNumber > 0 && { repostingNumber }),
      withoutCharge: reqMirrorData?.withoutCharge ?? false,
      jobModel: {
        locations: reqMirrorData?.addresses ?? [],
        vacancies: reqMirrorData?.vacancies,
        ...(reqMirrorData?.salaryMin && { salaryMin: reqMirrorData.salaryMin }),
        ...(reqMirrorData?.salaryMax && { salaryMax: reqMirrorData.salaryMax }),
        ...(reqMirrorData?.salaryConfidential && { salaryConfidential: reqMirrorData.salaryConfidential }),
        ...(reqMirrorData?.workPlacement && { workPlacement: reqMirrorData.workPlacement }),
        ...(reqMirrorData?.workPlacement === 'REMOTE' && { remote: true }),
        ...(reqMirrorData?.domain && { domain: reqMirrorData.domain }),
        ...(reqMirrorData?.ownApplyUrl && { ownApplyUrl: reqMirrorData.ownApplyUrl }),
      },
    },
    onDataChanged: (currentData) => {
      // Update the mirrorData with the price data
      setMirrorData?.({ pricingData: currentData }, false, true);
    },
  });

  // Destructure price data
  const {
    credits = 0,
    missingCredits = 0,
    voucherDiscountCredits = 0,
    vacanciesDiscount = false,
    discounts = [],
    walletUsage,
  } = priceData || {};

  // Considering that the negative balance is included in missingCredits,
  // we must pick the smallest number between the ad price and missingCredits
  const amountDue = Math?.min(credits, missingCredits);

  const totalDiscountAmount = discounts?.reduce((acc, discount) => acc + (discount.amount || 0), 0) ?? 0;


  // Render component
  // *************************
  return (
    <div className="mb-1 mt-5 divide-y divide-dashed divide-input border-y border-input px-4 sm:px-5">
      {/* Reposting number */}
      <div className={`flex items-center justify-between font-semibold ${repostingCompatible ? 'py-3.5' : 'py-5.5'}`}>
        <span>{activationPeriod}</span>
        {repostingCompatible && (
          <RepostingNumber
            record={record}
            setRepostingNumber={setRepostingNumber}
            initialRepostingIndex={initialRepostingNumber}
            setMirrorData={setMirrorData}
            countFromOne={!inActivePeriod}
            removeFromSubmit={removeRepostingFromSubmit}
          />
        )}
      </div>

      {/* Total in euro - disabled until fixed */}
      {/* <div className="flex items-center justify-between py-2 font-semibold"> */}
      {/*  <span>{t('job.add.label.total_euro')}</span> */}
      {/*  <span className="text-right text-lg font-bold"> */}
      {/*    <span className={`flex items-center justify-end gap-2 ${isLoading ? 'text-ink-light' : 'text-ink'}`}> */}
      {/*      {isLoading && <TailwindSpinner className="size-3.5" />} */}
      {/*      {euroPriceRaw || '0'} */}
      {/*    </span> */}
      {/*    <span className="-mt-1 block text-xs font-normal text-ink-light"> */}
      {/*      {t('price.info.vat.not.included')} */}
      {/*    </span> */}
      {/*  </span> */}
      {/* </div> */}

      {/* Ad price - for flex prices (percent) use credits, it calculates the price based on salary */}
      <div className="flex items-center justify-between py-2 font-semibold">
        <span className="flex items-baseline gap-1">{t('job.add.label.ad_price')}</span>
        <span className="text-right text-lg font-bold">
          <span className={`flex items-center justify-end gap-2 ${isLoading ? 'text-ink-light' : 'text-ink'}`}>
            {isLoading && <TailwindSpinner className="size-3.5" />}
            {selectedActivation?.identifier?.split('-')?.pop() === 'flex' ? credits + totalDiscountAmount : selectedActivation?.credits || '0'}
            <span className="ml-1 block text-pico">{t('global.label.neuro.text')}</span>

          </span>
        </span>
      </div>

      {/* Discounts, if available */}
      {discounts && discounts?.length > 0 && discounts.map((discount: ApiSchema<'PriceDiscount'>) => (
        <div key={discount?.label || discount?.amount} className="flex items-center justify-between py-2 font-semibold">
          <span>{t(discount.label ?? '')}</span>
          <span className="text-right text-lg font-bold">
            <span className="flex items-center justify-end gap-2 text-secondary">
              {discount.amount}
              <span className="block text-sm">{t('global.label.neuro.text')}</span>
            </span>
          </span>
        </div>
      ))}

      {/* Payment from wallet, if available */}
      {walletUsage && walletUsage !== '0' && (
        <div className="flex items-center justify-between py-2 font-semibold">
          <span>{t('job.add.label.payment_from_wallet')}</span>
          <span className="text-right text-lg font-bold">
            <span className="flex items-center justify-end gap-2 text-secondary">
              {walletUsage}
              <span className="block text-sm">{t('global.label.neuro.text')}</span>
            </span>
          </span>
        </div>
      )}

      {/* Amount due (the remaining credits to pay) */}
      <div className="flex items-center justify-between py-3.5 font-semibold">
        <span>{t('job.add.label.amount_due')}</span>
        <span
          className={`flex items-center gap-2.5 text-2xl font-bold ${isLoading ? 'text-ink-light' : 'text-secondary'}`}
        >
          {isLoading && <TailwindSpinner className="size-4.5" />}
          {amountDue || '0'}
          <span className="block text-sm">{t('global.label.neuro.text')}</span>
        </span>
      </div>

      {/* Info messages */}
      {(voucherDiscountCredits > 0 || vacanciesDiscount) && (
        <div className="flex flex-col gap-2 py-2">
          {/* Voucher discount credits */}
          {voucherDiscountCredits > 0 && (
            <div className="flex items-start gap-1.5 rounded bg-secondary/10 p-2">
              <PercentBadgeIcon className="size-5 shrink-0 text-secondary" />
              {t('job.add.voucher.discount.credits', { voucherDiscountCredits })}
            </div>
          )}
          {/* Vacancies discount */}
          {vacanciesDiscount && (
            <div className="flex items-start gap-1.5 rounded bg-secondary/10 p-2">
              <PercentBadgeIcon className="size-5 shrink-0 text-secondary" />
              {t('hire_bundle.vacancies.benefit')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
